import { useCallback, useEffect, useRef, useState } from "react"
import type { RefObject } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { API } from "@/api/client"
import type { LocalAudioRecord } from "../types"
import { patchProfileMeta } from "@/entities/profile"
import { createUploadQueue, type UploadQueue } from "./chunkUploadQueue"
import {
    deleteSession,
    getSession,
    getSessionChunks,
    requestPersistentStorage,
    saveChunk,
    saveSession,
    updateSessionFields,
} from "@/lib/chunkDb"
import { silentError } from "@/shared/lib/silentError"
import { settled } from "@/shared/lib/settled"
import { formatMsToMmSs } from "../utils"
import { formatDateShort, formatTimeHm } from "@/shared/lib/format"
import {
    initialRecordingState,
    recordingReducer,
    type RecordingEvent,
    type UploadState,
} from "./recordingMachine"
import { recoverOrphanSessions } from "./recoverOrphanSessions"
import { DEMO_QUOTA_EVENT } from "@/shared/lib/demoQuota"

const UPLOAD_ERROR_MSG = "Ошибка при загрузке. Проверьте интернет."
const INCOMPLETE_MSG = "Часть записи не загрузилась. Нажмите, чтобы повторить."

type Runtime = {
    uploader: UploadQueue | null
    activeLocalId: string | null
    creationBySession: Map<string, string>
}

type BridgeArgs = {
    api: API<unknown>
    selectedProfileId: string
    copilotSessionIdRef?: RefObject<string | null>
    username: string | undefined
    localAudioByProfileIdRef: { current: Record<string, LocalAudioRecord[]> }
    setLocalAudioByProfileId: (
        updater: (prev: Record<string, LocalAudioRecord[]>) => Record<string, LocalAudioRecord[]>,
    ) => void
    updateLocalRecord: (localId: string, patch: (r: LocalAudioRecord) => LocalAudioRecord) => void
    ownerProfileIdOf: (localId: string) => string | undefined
}

/** Browser/queue events → recordingMachine; side effects from phase transitions. */
export function useRecordingMachineBridge({
    api,
    selectedProfileId,
    copilotSessionIdRef,
    username,
    localAudioByProfileIdRef,
    setLocalAudioByProfileId,
    updateLocalRecord,
    ownerProfileIdOf,
}: BridgeArgs) {
    const queryClient = useQueryClient()
    const [, setMachine] = useState(initialRecordingState)
    const machineRef = useRef(initialRecordingState)
    const runtimeRef = useRef<Runtime>({
        uploader: null,
        activeLocalId: null,
        creationBySession: new Map(),
    })

    const setUploadError = useCallback(
        (localId: string, message: string) => {
            updateLocalRecord(localId, (r) => ({
                ...r,
                isUploading: false,
                isFinalizing: false,
                uploadError: message,
            }))
        },
        [updateLocalRecord],
    )

    const { mutateAsync: completeUploadAsync } = useMutation({
        mutationFn: async (data: {
            sessionId: string
            totalChunks: number
            profileId: string
            creationDate: string
            localId: string
            copilotSessionId?: string
        }) => {
            const response = await api.v1.completeUpload(data.sessionId, {
                profile_key: data.profileId,
                creation_date: data.creationDate,
                total_parts: data.totalChunks,
                copilot_session_id: data.copilotSessionId,
            })
            return { data, fileId: response.data?.file_id ?? undefined, name: response.data?.name }
        },
        retry: 2,
        retryDelay: (attempt) => 100 * (attempt + 1),
        meta: { silent: true },
        onSuccess: async ({ data, fileId, name }) => {
            runtimeRef.current.uploader?.stop()
            await deleteSession(data.sessionId)
            patchProfileMeta(queryClient, ownerProfileIdOf(data.localId) ?? data.profileId, {
                isInDatabase: true,
            })
            updateLocalRecord(data.localId, (r) => ({
                ...r,
                isUploading: false,
                uploadError: undefined,
                file_id: fileId,
                name: name ?? r.name,
            }))
        },
        onError: (_err, data) => setUploadError(data.localId, UPLOAD_ERROR_MSG),
    })

    const runFinalize = useCallback(
        async (localId: string, upload: UploadState) => {
            const creationDate =
                runtimeRef.current.creationBySession.get(upload.sessionId) ??
                new Date().toISOString()
            const record = Object.values(localAudioByProfileIdRef.current)
                .flat()
                .find((r) => r.id === localId)
            const done = await settled(
                completeUploadAsync({
                    sessionId: upload.sessionId,
                    totalChunks: upload.totalChunks,
                    profileId: upload.profileId,
                    creationDate,
                    localId,
                    copilotSessionId:
                        record?.copilotSessionId ?? copilotSessionIdRef?.current ?? undefined,
                }),
            )
            const next = recordingReducer(machineRef.current, {
                type: done.ok ? "finalizeSucceeded" : "finalizeFailed",
                localId,
            })
            machineRef.current = next
            setMachine(next)
        },
        [completeUploadAsync, copilotSessionIdRef, localAudioByProfileIdRef],
    )

    const send = useCallback(
        (event: RecordingEvent) => {
            const prev = machineRef.current
            const next = recordingReducer(prev, event)
            machineRef.current = next
            setMachine(next)
            for (const [localId, upload] of Object.entries(next.uploads)) {
                const before = prev.uploads[localId]
                if (upload.phase === "finalizing" && before?.phase !== "finalizing") {
                    void runFinalize(localId, upload)
                }
                if (
                    (upload.phase === "failed_incomplete" || upload.phase === "failed_finalize") &&
                    before?.phase !== upload.phase
                ) {
                    setUploadError(
                        localId,
                        upload.phase === "failed_finalize" ? UPLOAD_ERROR_MSG : INCOMPLETE_MSG,
                    )
                }
            }
        },
        [runFinalize, setUploadError],
    )

    const buildUploader = useCallback(
        (sessionId: string): UploadQueue =>
            createUploadQueue(api, sessionId, () => copilotSessionIdRef?.current ?? undefined, {
                onChunkUploaded: () => {
                    const localId = runtimeRef.current.activeLocalId
                    send(localId ? { type: "chunkUploaded", localId } : { type: "chunkUploaded" })
                },
                onDrained: () => {
                    const localId = runtimeRef.current.activeLocalId
                    if (localId) send({ type: "drained", localId })
                },
                onDeadLetter: () => {
                    const localId = runtimeRef.current.activeLocalId
                    send(localId ? { type: "deadLetter", localId } : { type: "deadLetter" })
                },
                onSessionClosed: () => {
                    const localId = runtimeRef.current.activeLocalId
                    send(localId ? { type: "sessionClosed", localId } : { type: "sessionClosed" })
                },
                onQuotaExceeded: () => {
                    send({ type: "quotaStop" })
                    window.dispatchEvent(new Event(DEMO_QUOTA_EVENT))
                },
            }),
        [api, copilotSessionIdRef, send],
    )

    const onRecordingStart = useCallback(
        (profileId: string) => {
            runtimeRef.current.uploader?.stop()
            void requestPersistentStorage()
            const sessionId = crypto.randomUUID()
            const creationDate = new Date().toISOString()
            runtimeRef.current = {
                uploader: buildUploader(sessionId),
                activeLocalId: null,
                creationBySession: new Map([
                    ...runtimeRef.current.creationBySession,
                    [sessionId, creationDate],
                ]),
            }
            send({ type: "recordingStarted", sessionId, profileId })
            saveSession({
                sessionId,
                profileId,
                creationDate,
                copilotSessionId: copilotSessionIdRef?.current ?? undefined,
            }).then(undefined, (err) => silentError("saveSession", err))
        },
        [buildUploader, copilotSessionIdRef, send],
    )

    const onChunkReady = useCallback(
        (blob: Blob, index: number) => {
            const sessionId = machineRef.current.activeRecording?.sessionId
            const uploader = runtimeRef.current.uploader
            if (!sessionId || !uploader) return
            send({ type: "chunkReady" })
            uploader.enqueue(index, blob)
            void saveChunk(sessionId, index, blob).then(undefined, (err) =>
                silentError("saveChunk", err),
            )
        },
        [send],
    )

    const onRecorded = useCallback(
        ({
            blob,
            durationSec,
            totalChunks,
        }: {
            blob: Blob
            durationSec: number
            totalChunks: number
        }) => {
            const active = machineRef.current.activeRecording
            if (
                active?.dropPending &&
                active.chunkCount - active.pendingChunks <= 0
            ) {
                send({ type: "recordingStopped", localId: "quota-empty", totalChunks: 0 })
                return
            }
            const sessionId = active?.sessionId
            const profileId = active?.profileId ?? selectedProfileId
            const creationDate = sessionId
                ? runtimeRef.current.creationBySession.get(sessionId)
                : undefined
            if (!profileId || !username || !sessionId || !creationDate) return

            const now = new Date()
            const localId = `${now.getTime()}`
            const copilotSessionId = copilotSessionIdRef?.current ?? undefined
            runtimeRef.current.activeLocalId = localId
            setLocalAudioByProfileId((prev) => ({
                ...prev,
                [profileId]: [
                    ...(prev[profileId] ?? []),
                    {
                        id: localId,
                        sessionId,
                        copilotSessionId,
                        name: "Новая аудиозапись",
                        url: URL.createObjectURL(blob),
                        blob,
                        durationSec,
                        duration: formatMsToMmSs(durationSec * 1000),
                        date: formatDateShort(now),
                        time: formatTimeHm(now),
                        createdAt: now.getTime(),
                        isUploading: true,
                    },
                ],
            }))
            updateSessionFields(sessionId, {
                totalChunks,
                ...(copilotSessionId ? { copilotSessionId } : {}),
            }).then(undefined, (err) => silentError("indexedDb", err))
            send({ type: "recordingStopped", localId, totalChunks })
        },
        [
            copilotSessionIdRef,
            selectedProfileId,
            send,
            setLocalAudioByProfileId,
            username,
        ],
    )

    const onRetryUploadLocalRecord = useCallback(
        async ({ profileId, localId }: { profileId: string; localId: string }) => {
            if (!profileId || !username) return
            const record = (localAudioByProfileIdRef.current[profileId] ?? []).find(
                (r) => r.id === localId,
            )
            if (!record?.sessionId) return

            setLocalAudioByProfileId((prev) => ({
                ...prev,
                [profileId]: (prev[profileId] ?? []).map((r) =>
                    r.id === localId ? { ...r, isUploading: true, uploadError: undefined } : r,
                ),
            }))

            const sessionId = record.sessionId
            // Remount / HMR: jotai may still hold the row while the machine is empty.
            if (!machineRef.current.uploads[localId]) {
                const seeded = {
                    ...machineRef.current,
                    uploads: {
                        ...machineRef.current.uploads,
                        [localId]: {
                            sessionId,
                            profileId,
                            phase: "failed_incomplete" as const,
                            totalChunks: 0,
                            pendingChunks: 0,
                            finalizeArmed: false,
                            deadLettered: false,
                            sessionClosed: false,
                        },
                    },
                }
                machineRef.current = seeded
                setMachine(seeded)
            }
            send({ type: "retryRequested", localId })

            const chunksResult = await settled(getSessionChunks(sessionId))
            const sessionResult = await settled(getSession(sessionId))
            if (!chunksResult.ok) {
                silentError("getSessionChunks", chunksResult.error)
                setUploadError(localId, INCOMPLETE_MSG)
                return
            }
            if (!sessionResult.ok) {
                silentError("getSession", sessionResult.error)
                setUploadError(localId, INCOMPLETE_MSG)
                return
            }

            const creationDate =
                sessionResult.value?.creationDate ?? new Date(record.createdAt).toISOString()
            runtimeRef.current.creationBySession.set(sessionId, creationDate)
            runtimeRef.current.activeLocalId = localId
            runtimeRef.current.uploader?.stop()
            runtimeRef.current.uploader = buildUploader(sessionId)

            const pending = chunksResult.value.filter((c) => !c.uploaded)
            send({
                type: "retryReady",
                localId,
                totalChunks: chunksResult.value.length,
                pendingChunks: pending.length,
            })
            for (const chunk of pending) {
                runtimeRef.current.uploader.enqueue(chunk.chunkIndex, chunk.blob)
            }
        },
        [
            buildUploader,
            localAudioByProfileIdRef,
            send,
            setLocalAudioByProfileId,
            setUploadError,
            username,
        ],
    )

    useEffect(() => {
        let cancelled = false
        recoverOrphanSessions(api, {
            isCancelled: () => cancelled,
            isActiveSession: (id) => machineRef.current.activeRecording?.sessionId === id,
        })
        return () => {
            cancelled = true
        }
    }, [api])

    return { onRecordingStart, onChunkReady, onRecorded, onRetryUploadLocalRecord }
}
